import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Flame, Minus, Plus, Lock, ChevronDown, Check } from "lucide-react";
import { toast } from "sonner";
import { CATEGORIES, getItems, LEVEL_SIZE, MAX_LEVEL } from "@/data/content";
import { BIGKIDS_CATEGORIES, getBKItems } from "@/data/bigkids";
import { setLevel } from "@/lib/api";

const AGE_BANDS = ["Ages 3–5", "Ages 4–6", "Ages 5–6"];

export default function ProgressModal({ open, onClose, profile, refreshProfile }) {
  const [saving, setSaving] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const bigKids = !!profile.big_kids;
  const cats = bigKids ? BIGKIDS_CATEGORIES : CATEGORIES;
  const itemsFor = (key, level) => (bigKids ? getBKItems(key, level) : getItems(key, level));

  const levelOf = (key) => profile.levels?.[key] || 1;
  const learnedInLevel = (key) => {
    const keys = itemsFor(key, levelOf(key)).map((i) => i.k);
    return (profile.progress?.[key] || []).filter((k) => keys.includes(k)).length;
  };
  const badges = cats.filter((c) => learnedInLevel(c.key) >= LEVEL_SIZE);

  const changeLevel = async (key, delta) => {
    const next = Math.min(MAX_LEVEL, Math.max(1, levelOf(key) + delta));
    if (next === levelOf(key)) return;
    setSaving(key);
    try {
      const p = await setLevel(profile.id, key, next);
      await refreshProfile(p.id);
      toast.success(`${cats.find((c) => c.key === key).title}: Level ${next}`);
    } catch (e) {
      toast.error("Could not change level");
    } finally {
      setSaving(null);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="progress-modal"
        >
          <motion.div
            initial={{ scale: 0.85, y: 30 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.85, opacity: 0 }}
            transition={{ type: "spring", stiffness: 220, damping: 18 }}
            className="bg-white rounded-[2rem] border-4 border-slate-900/10 p-8 w-full max-w-md max-h-[88vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-fredoka text-3xl font-bold text-slate-900">Parent Area</h2>
              <button
                data-testid="close-progress-button"
                onClick={onClose}
                className="w-11 h-11 rounded-full bg-slate-100 flex items-center justify-center"
              >
                <X size={24} strokeWidth={3} className="text-slate-500" />
              </button>
            </div>
            <p className="text-base font-bold text-slate-400 mb-5">
              {profile.name}'s progress &amp; learning levels
            </p>

            <div className="flex items-center gap-3 mb-6">
              <div
                className="flex items-center gap-2 bg-orange-100 text-orange-600 font-bold px-4 py-2.5 rounded-2xl"
                data-testid="modal-streak"
              >
                <Flame size={22} strokeWidth={3} className="fill-orange-400 text-orange-500" />
                <span className="text-lg">{profile.streak || 0} day streak</span>
              </div>
              <div
                className="flex items-center gap-2 bg-yellow-100 text-yellow-700 font-bold px-4 py-2.5 rounded-2xl"
                data-testid="modal-badge-count"
              >
                <span className="text-lg">⭐ {badges.length} badge{badges.length === 1 ? "" : "s"}</span>
              </div>
            </div>

            <p className="text-sm font-bold uppercase tracking-widest text-slate-400 mb-3">
              Progress &amp; Level
            </p>
            <div className="space-y-4">
              {cats.map((c) => {
                const lvl = levelOf(c.key);
                const locked = c.premium && !profile.premium;
                const learned = learnedInLevel(c.key);
                const pct = Math.round((learned / LEVEL_SIZE) * 100);
                return (
                  <div key={c.key} data-testid={`progress-row-${c.key}`}>
                    <div className="flex items-center justify-between mb-1.5 gap-2">
                      <span className="font-fredoka text-lg font-bold text-slate-700 flex items-center gap-2 min-w-0">
                        <span className="text-2xl shrink-0">{c.emoji}</span>
                        <span className="truncate">{c.title}</span>
                        {locked && <Lock size={15} strokeWidth={3} className="text-slate-400 shrink-0" />}
                      </span>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <button
                          data-testid={`level-down-${c.key}`}
                          onClick={() => changeLevel(c.key, -1)}
                          disabled={saving === c.key || lvl <= 1}
                          className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center disabled:opacity-40"
                        >
                          <Minus size={16} strokeWidth={3} className="text-slate-600" />
                        </button>
                        <span
                          data-testid={`level-value-${c.key}`}
                          className="font-fredoka font-bold text-slate-800 w-14 text-center text-sm"
                        >
                          Lv {lvl}
                        </span>
                        <button
                          data-testid={`level-up-${c.key}`}
                          onClick={() => changeLevel(c.key, 1)}
                          disabled={saving === c.key || lvl >= MAX_LEVEL}
                          className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center disabled:opacity-40"
                        >
                          <Plus size={16} strokeWidth={3} className="text-white" />
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-4 rounded-full bg-slate-100 overflow-hidden">
                        <motion.div
                          className="h-full rounded-full"
                          style={{ backgroundColor: c.color }}
                          initial={{ width: 0 }}
                          animate={{ width: `${pct}%` }}
                          transition={{ type: "spring", stiffness: 120, damping: 20 }}
                        />
                      </div>
                      <span className="font-bold text-slate-500 text-sm w-10 text-right">
                        {learned}/{LEVEL_SIZE}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-xs font-bold text-slate-400 mt-5 text-center">
              Raise the level to unlock harder items (Level 1 → {MAX_LEVEL}).
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
